/** Accepts a bare hostname ("acme.vn") by retrying with an `https://` prefix — same forgiving
 * behavior a visitor typing a URL by hand expects. Returns `null` when neither form parses to
 * an http(s) URL (matches `httpUrl` in packages/validation/src/sources.ts).
 *
 * Shared by `WebsiteStep` (a new bot's URL, step 1) and `CrawlStep` (replacing a failed
 * source's URL on an already-created bot, step 2's "Try a different address" — prod-copy-fixes
 * cycle, defect 3) — both need identical forgiveness so a value one wizard step accepts isn't
 * rejected by the other. Originally lived only in `WebsiteStep.tsx`; extracted here once a
 * second call site needed it rather than growing a second, silently-drifting copy. */
export function normalizeUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const candidates = trimmed.includes('://') ? [trimmed] : [`https://${trimmed}`];
  for (const candidate of candidates) {
    try {
      const parsed = new URL(candidate);
      if (parsed.protocol === 'http:' || parsed.protocol === 'https:') return parsed.toString();
    } catch {
      // try the next candidate (or fall through to null below)
    }
  }
  return null;
}
